import { Injectable } from '@angular/core';

export interface UserFriendlyError {
  code: string;
  message: string;
  isUserError: boolean;
  recovery?: string[];
  details?: any;
}

interface ErrorDef {
  msg: string;
  user: boolean;
  rec?: string[];
}

@Injectable({ providedIn: 'root' })
export class CallErrorHandler {

  private readonly errors: Record<string, ErrorDef> = {
    // User Errors
    INVALID_PHONE_NUMBER: {
      msg: 'Invalid phone number format. Please enter a valid phone number with at least 10 digits.',
      user: true,
      rec: ['Check phone number format', 'Include country code if international', 'Ensure at least 10 digits']
    },
    MICROPHONE_PERMISSION_REQUIRED: {
      msg: 'Microphone permission is required to make calls. Please allow microphone access in your browser settings.',
      user: true,
      rec: ['Click microphone icon in browser address bar', 'Select "Allow" for permissions', 'Refresh page after enabling']
    },

    // WebRTC Connection Errors
    WEBRTC_NOT_CONNECTED: {
      msg: 'Call service is not available. Please check your internet connection and try again.',
      user: false,
      rec: ['Check internet connection', 'Wait a moment and try again', 'Refresh page if problem persists']
    },
    AUTH_ERROR: {
      msg: 'WebRTC authentication failed - invalid credentials. Please refresh the page.',
      user: false,
      rec: ['Refresh the page', 'Log out and log back in', 'Contact support if problem persists']
    },
    NETWORK_ERROR: {
      msg: 'Network connectivity issue - check internet connection.',
      user: false,
      rec: ['Check internet connection', 'Try area with better connectivity', 'Wait for connection to stabilize']
    },
    SIP_REGISTRATION_ERROR: {
      msg: 'SIP registration failed - check account status.',
      user: false,
      rec: ['Verify account is active', 'Check calling permissions', 'Contact administrator']
    },
    CONNECTION_ERROR: {
      msg: 'WebRTC connection error. Please try again.',
      user: false,
      rec: ['Check internet connection', 'Refresh the page', 'Contact support if error continues']
    },

    // Backend Errors
    BACKEND_SAVE_FAILED: {
      msg: 'Call completed but failed to save to records. The call was successful but may not appear in call history.',
      user: false,
      rec: ['Call was still successful', 'Manually add note if needed']
    },
    BACKEND_VALIDATION_ERROR: {
      msg: 'Call data validation failed. Please contact support if this persists.',
      user: false,
      rec: ['Try the operation again', 'Contact support with error details']
    },

    // Call Operation Errors
    CALL_INITIATION_FAILED: {
      msg: 'Failed to start the call. Please check your connection and try again.',
      user: false,
      rec: ['Verify phone number', 'Check internet connection', 'Ensure microphone permissions', 'Try again in a moment']
    },
    CANCELLATION_NOTIFICATION_FAILED: {
      msg: 'Call was cancelled successfully but notification failed (non-critical).',
      user: false
    }
  };

  private readonly sipErrors: Record<number, string> = {
    200: 'Call connected successfully',
    302: 'Call redirected', 400: 'Invalid call request', 401: 'Authentication required',
    403: 'Call forbidden', 404: 'Number not found', 408: 'Request timeout - no answer',
    480: 'Temporarily unavailable', 486: 'Line busy', 487: 'Call cancelled',
    500: 'Server error', 503: 'Service unavailable', 600: 'Busy everywhere',
    603: 'Call declined', 604: 'Does not exist'
  };

  /**
   * Process any error and return user-friendly error details
   */
  processError(error: any, code?: string): UserFriendlyError {
    const errorCode = code || error?.code || 'UNKNOWN_ERROR';
    const errorDef = this.errors[errorCode];

    if (errorDef) {
      return {
        code: errorCode,
        message: errorDef.msg,
        isUserError: errorDef.user,
        recovery: errorDef.rec,
        details: error
      };
    }

    return {
      code: errorCode,
      message: error?.message || 'An unexpected error occurred during the call.',
      isUserError: false,
      recovery: ['Try the operation again', 'Refresh the page if problem persists'],
      details: error
    };
  }

  /**
   * Get user-friendly message for SIP error codes
   */
  getSIPErrorMessage(sipCode: number): string {
    return this.sipErrors[sipCode] || `SIP error ${sipCode}`;
  }

  /**
   * Get media error message based on MediaError or DOMException
   */
  getMediaErrorMessage(error: any): string {
    if (!error) return 'Unknown media error';

    const name = error.name || '';
    const code = error.code;

    // DOMException permission errors
    const errorMessages: Record<string, string> = {
      NotAllowedError: 'Microphone permission denied. Please allow microphone access in browser settings.',
      PermissionDeniedError: 'Microphone permission denied. Please allow microphone access in browser settings.',
      NotFoundError: 'No microphone found. Please connect a microphone and try again.',
      DevicesNotFoundError: 'No microphone found. Please connect a microphone and try again.',
      NotReadableError: 'Microphone is already in use by another application.',
      TrackStartError: 'Microphone is already in use by another application.',
      OverconstrainedError: 'Requested audio settings not supported by your device.',
      ConstraintNotSatisfiedError: 'Requested audio settings not supported by your device.',
      TypeError: 'Invalid audio constraints. Please check device settings.',
      AbortError: 'Microphone access was aborted. Please try again.'
    };

    if (errorMessages[name]) return errorMessages[name];

    // MediaError codes
    const mediaCodeMessages: Record<number, string> = {
      1: 'Media loading aborted',
      2: 'Network error while loading media',
      3: 'Media decoding failed',
      4: 'Media format not supported'
    };

    if (code && mediaCodeMessages[code]) return mediaCodeMessages[code];

    return error.message || 'Media error occurred';
  }

  /**
   * Check if error code represents a user error
   */
  isUserError(code: string): boolean {
    return this.errors[code]?.user || false;
  }

  /**
   * Get recovery suggestions for an error code
   */
  getRecoverySuggestions(code: string): string[] {
    return this.errors[code]?.rec || [];
  }

  /**
   * Determine if SIP code represents a user error (4xx) vs system error (5xx+)
   */
  isSIPUserError(sipCode: number): boolean {
    return sipCode >= 400 && sipCode < 500;
  }

  /**
   * Determine error severity for UI presentation
   */
  getErrorSeverity(code: string): 'low' | 'medium' | 'high' | 'critical' {
    const severity: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
      AUTH_ERROR: 'critical',
      MICROPHONE_PERMISSION_REQUIRED: 'critical',
      WEBRTC_NOT_CONNECTED: 'high',
      NETWORK_ERROR: 'high',
      SIP_REGISTRATION_ERROR: 'high',
      CALL_INITIATION_FAILED: 'medium',
      BACKEND_VALIDATION_ERROR: 'medium',
      INVALID_PHONE_NUMBER: 'medium'
    };

    return severity[code] || 'low';
  }
}
